import { addDays, addMonths, addYears, startOfDay, subYears } from 'date-fns';
import { TFunction } from 'i18next';
import { z } from 'zod';
import { htmlDecode } from '../../common/utils/decodeHtml';
import { getCountryLabel } from '../../common/utils/getCountryLabel';
import {
  APT_OR_SUITE_MAX_LENGTH,
  CITY_MAX_LENGTH,
  COMPANY_NAME_MAX_LENGTH,
  COUNTRIES_REQUIRE_PROVINCES,
  DEFAULT_MIN_STRING_LENGTH,
  EMAIL_MAX_LENGTH,
  GIC_FUND_SOURCE_LIST,
  GIC_FUND_SOURCE_OTHER_MAX_LENGTH,
  GIVEN_NAME_MAX_LENGTH,
  ISSUING_PLACE_OR_AUTHORITY_MAX_LENGTH,
  NUMBER_AND_STREET_NAME_MAX_LENGTH,
  OCCUPATIONS_LIST,
  OCCUPATION_OTHER_MAX_LENGTH,
  OTHER_OCCUPATION,
  PASSPORT_NUMBER_MAX_LENGTH,
  PHONE_NUMBER_MAX_LENGTH,
  POLITICALLY_IMPORTANT_FULL_NAME_MAX_LENGTH,
  POLITICALLY_IMPORTANT_RELATIONSHIP_MAX_LENGTH,
  POLITICALLY_IMPORTANT_ROLE_MAX_LENGTH,
  POSTAL_CODE_MAX_LENGTH,
  PROVINCE_MAX_LENGTH,
  RBC_EMAIL_REGEX,
  RBC_GIC_FORM_PROPERTIES,
  RBC_NUMBER_AND_STREET_REGEX,
  SCHOOL_MAX_LENGTH,
  SOURCE_OF_WEALTH_LIST,
  SOURCE_OF_WEALTH_OTHER_MAX_LENGTH,
  STUDENT_NOT_WORKING_VALUES,
  STUDENT_WORKING_VALUES,
  SUPPORTED_FILE_TYPES_STR,
  SUPPORTED_MIME_TYPES,
  SURNAME_MAX_LENGTH,
  VALID_EMPLOYMENT_STATUS_VALUES,
  VALID_FULL_COUNTRY_VALUES,
  VALID_GIC_ACCEPTED_COUNTRY_VALUES,
  VALID_MONTH_VALUES,
  VALID_PREFIX_VALUES,
  VALID_PROVINCE_VALUES,
} from './constants';

export const prefixSchema = (t: TFunction) =>
  z.preprocess(
    value => (!value ? undefined : value),
    z
      .enum(VALID_PREFIX_VALUES, {
        required_error: t('rbcv2.errors.fieldRequired', {
          fieldName: t('rbcv2.fieldLabels.prefix'),
        }),
        invalid_type_error: t('rbcv2.errors.valid', {
          fieldName: t('rbcv2.fieldLabels.prefix'),
        }),
      })
      .optional(),
  );

export const givenNameSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.givenName'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.givenName'),
      }),
    })
    .trim()
    .max(
      GIVEN_NAME_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.givenName'),
        maxLength: GIVEN_NAME_MAX_LENGTH,
      }),
    )
    .refine(value => !!value.match(/^([^0-9]*)$/g), t('rbcv2.errors.numbersNotAllowed'))
    .optional();

export const surnameSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.surname'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.surname'),
      }),
    })
    .trim()
    .max(
      SURNAME_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.surname'),
        maxLength: SURNAME_MAX_LENGTH,
      }),
    )
    .refine(value => !!value.match(/^([^0-9]*)$/g), t('rbcv2.errors.numbersNotAllowed'))
    .optional();

export const studentNameSchema = (t: TFunction) =>
  z
    .object({
      givenName: z.string().optional(),
      surname: z.string().optional(),
    })
    .refine(
      data => !!data.givenName?.trim() || !!data.surname?.trim(),
      t('rbcv2.errors.eitherGivenNameOrSurname', {
        surname: t('rbcv2.fieldLabels.surname'),
        givenName: t('rbcv2.fieldLabels.givenName'),
      }),
    );

export const dateOfBirthSchema = (t: TFunction) => {
  return z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.dateOfBirth'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.dateOfBirth'),
      }),
    })
    .refine(
      dobString => {
        const expectedDateCutoff = startOfDay(subYears(new Date(), 14));
        const dob = new Date(dobString);
        const birthday = startOfDay(dob);
        return !isNaN(dob.getTime()) && birthday <= expectedDateCutoff;
      },
      {
        message: t('rbcv2.errors.ageRestriction'),
      },
    );
};

export const purposeSchema = (t: TFunction) =>
  z.enum(['Living expenses', 'School/tuition', 'Investment/savings'], {
    required_error: t('rbcv2.errors.fieldRequired', {
      fieldName: t('rbcv2.fieldLabels.purpose'),
    }),
    invalid_type_error: t('rbcv2.errors.validSelection'),
  });

export const employmentStatusSchema = (t: TFunction) =>
  z.enum(VALID_EMPLOYMENT_STATUS_VALUES, {
    required_error: t('rbcv2.errors.fieldRequired', {
      fieldName: t('rbcv2.fieldLabels.employmentStatus'),
    }),
    invalid_type_error: t('rbcv2.errors.valid', {
      fieldName: t('rbcv2.fieldLabels.employmentStatus'),
    }),
  });

export const levelOfStudyNotWorkingSchema = (t: TFunction) =>
  z.enum(STUDENT_NOT_WORKING_VALUES, {
    required_error: t('rbcv2.errors.fieldRequired', {
      fieldName: t('rbcv2.fieldLabels.levelOfStudy'),
    }),
    invalid_type_error: t('rbc.errors.valid', {
      fieldName: t('rbcv2.fieldLabels.levelOfStudy'),
    }),
  });

export const levelOfStudyWorkingSchema = (t: TFunction) =>
  z.enum(STUDENT_WORKING_VALUES, {
    required_error: t('rbcv2.errors.fieldRequired', {
      fieldName: t('rbcv2.fieldLabels.levelOfStudy'),
    }),
    invalid_type_error: t('rbc.errors.valid', {
      fieldName: t('rbcv2.fieldLabels.levelOfStudy'),
    }),
  });

export const levelOfStudySchema = (t: TFunction) =>
  z.union(
    [
      z.enum(STUDENT_NOT_WORKING_VALUES),
      z.enum(STUDENT_WORKING_VALUES),
      z.literal(undefined),
      z.literal(null),
      z.literal(''),
    ],
    {
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.levelOfStudy'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.levelOfStudy'),
      }),
    },
  );

export const occupationSchema = (t: TFunction) =>
  z.union(
    [
      z.enum(OCCUPATIONS_LIST as unknown as readonly [string, ...string[]]),
      z.literal(null),
      z.literal(undefined),
      z.literal(''),
    ],
    {
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.occupation'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.occupation'),
      }),
    },
  );

export const occupationOtherSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.occupationOther'),
      }),
    })
    .max(
      OCCUPATION_OTHER_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.occupationOther'),
        maxLength: OCCUPATION_OTHER_MAX_LENGTH,
      }),
    );

export const otherCountryResidentForTaxCountry1Schema = (t: TFunction) =>
  z.union(
    [z.enum(VALID_FULL_COUNTRY_VALUES), z.literal(null), z.literal(undefined), z.literal('')],
    {
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.otherCountryResidentForTaxCountry1'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.otherCountryResidentForTaxCountry1'),
      }),
    },
  );
export const otherCountryResidentForTaxCountry2Schema = (t: TFunction) =>
  z
    .union(
      [z.enum(VALID_FULL_COUNTRY_VALUES), z.literal(null), z.literal(undefined), z.literal('')],
      {
        invalid_type_error: t('rbcv2.errors.valid', {
          fieldName: t('rbcv2.fieldLabels.otherCountryResidentForTaxCountry2'),
        }),
      },
    )
    .optional();
export const otherCountryResidentForTaxCountry3Schema = (t: TFunction) =>
  z
    .union(
      [z.enum(VALID_FULL_COUNTRY_VALUES), z.literal(null), z.literal(undefined), z.literal('')],
      {
        invalid_type_error: t('rbcv2.errors.valid', {
          fieldName: t('rbcv2.fieldLabels.otherCountryResidentForTaxCountry3'),
        }),
      },
    )
    .optional();

export const companyNameSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.companyName'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.companyName'),
      }),
    })
    // Conditionally Rendered fields cannot have a min length
    .max(
      COMPANY_NAME_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.companyName'),
        maxLength: COMPANY_NAME_MAX_LENGTH,
      }),
    );

export const companyPhoneNumberSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.companyPhoneNumber'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.companyPhoneNumber'),
      }),
    })
    // Conditionally Rendered fields cannot have a min length
    .max(
      PHONE_NUMBER_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.companyPhoneNumber'),
        maxLength: PHONE_NUMBER_MAX_LENGTH,
      }),
    );

export const isMobileNumberSchema = (t: TFunction) =>
  z.boolean({
    invalid_type_error: t('rbcv2.errors.valid', {
      fieldName: t('rbcv2.fieldLabels.isMobileNumber'),
    }),
  });

export const phoneNumberSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.phoneNumber'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.phoneNumber'),
      }),
    })
    .min(
      DEFAULT_MIN_STRING_LENGTH,
      t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.phoneNumber'),
      }),
    )
    .max(
      PHONE_NUMBER_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.phoneNumber'),
        maxLength: PHONE_NUMBER_MAX_LENGTH,
      }),
    );

export const emailAddressSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.emailAddress'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.emailAddress'),
      }),
    })
    .trim()
    .email(
      t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.emailAddress'),
      }),
    )
    .regex(
      RBC_EMAIL_REGEX,
      t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.emailAddress'),
      }),
    )
    .min(
      DEFAULT_MIN_STRING_LENGTH,
      t('rbcv2.errors.fieldRequired', { fieldName: t('rbcv2.fieldLabels.emailAddress') }),
    )
    .max(
      EMAIL_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.emailAddress'),
        maxLength: EMAIL_MAX_LENGTH,
      }),
    );

export const confirmEmailAddressSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.confirmEmailAddress'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.confirmEmailAddress'),
      }),
    })
    .trim()
    .email(
      t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.confirmEmailAddress'),
      }),
    )
    .min(
      DEFAULT_MIN_STRING_LENGTH,
      t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.confirmEmailAddress'),
      }),
    )
    .max(
      EMAIL_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.confirmEmailAddress'),
        maxLength: EMAIL_MAX_LENGTH,
      }),
    );

export const aptOrSuiteSchema = (t: TFunction) =>
  z
    .string({
      invalid_type_error: htmlDecode(
        t('rbcv2.errors.valid', {
          fieldName: t('rbcv2.fieldLabels.aptOrSuite'),
        }),
      ),
    })
    .max(
      APT_OR_SUITE_MAX_LENGTH,
      htmlDecode(
        t('rbcv2.errors.maxLength', {
          fieldName: t('rbcv2.fieldLabels.aptOrSuite'),
          maxLength: APT_OR_SUITE_MAX_LENGTH,
        }),
      ),
    );

export const numberAndStreetNameSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.numberAndStreetName'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.numberAndStreetName'),
      }),
    })
    .trim()
    .regex(
      RBC_NUMBER_AND_STREET_REGEX,
      t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.numberAndStreetName'),
      }),
    )
    .min(
      DEFAULT_MIN_STRING_LENGTH,
      t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.numberAndStreetName'),
      }),
    )
    .max(
      NUMBER_AND_STREET_NAME_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.numberAndStreetName'),
        maxLength: NUMBER_AND_STREET_NAME_MAX_LENGTH,
      }),
    );

export const citySchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.city'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.city'),
      }),
    })
    .trim()
    .min(
      DEFAULT_MIN_STRING_LENGTH,
      t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.city'),
      }),
    )
    .max(
      CITY_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.city'),
        maxLength: CITY_MAX_LENGTH,
      }),
    );

export const stateOrProvinceSchema = (t: TFunction) =>
  z
    .string({
      invalid_type_error: htmlDecode(
        t('rbcv2.errors.valid', {
          fieldName: t('rbcv2.fieldLabels.stateOrProvince'),
        }),
      ),
    })
    .max(
      PROVINCE_MAX_LENGTH,
      htmlDecode(
        t('rbcv2.errors.maxLength', {
          fieldName: t('rbcv2.fieldLabels.stateOrProvince'),
          maxLength: PROVINCE_MAX_LENGTH,
        }),
      ),
    );

export const countrySchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.country'),
      }),
    })
    .refine(
      value => VALID_GIC_ACCEPTED_COUNTRY_VALUES.includes(value),
      value => {
        return {
          message: `${t('rbcv2.errors.invalidCountry')}: ${getCountryLabel(value)}`,
          path: [],
        };
      },
    );

export const postalCodeSchema = (t: TFunction) =>
  z
    .string({
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.postalCode'),
      }),
    })
    .max(
      POSTAL_CODE_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.postalCode'),
        maxLength: POSTAL_CODE_MAX_LENGTH,
      }),
    );

export const passportNumberSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.passportNumber'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.passportNumber'),
      }),
    })
    .trim()
    .min(
      DEFAULT_MIN_STRING_LENGTH,
      t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.passportNumber'),
      }),
    )
    .max(
      PASSPORT_NUMBER_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.passportNumber'),
        maxLength: PASSPORT_NUMBER_MAX_LENGTH,
      }),
    );

export const issuingPlaceOrAuthoritySchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.issuingPlaceOrAuthority'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.issuingPlaceOrAuthority'),
      }),
    })
    .trim()
    .min(
      DEFAULT_MIN_STRING_LENGTH,
      t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.issuingPlaceOrAuthority'),
      }),
    )
    .max(
      ISSUING_PLACE_OR_AUTHORITY_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.issuingPlaceOrAuthority'),
        maxLength: ISSUING_PLACE_OR_AUTHORITY_MAX_LENGTH,
      }),
    );

export const expiryDateSchema = (t: TFunction) => {
  const isOneMonthAhead = (dateString: string) => {
    const expiryDate = new Date(dateString);
    const oneMonthAhead = startOfDay(addMonths(new Date(), 1));
    return expiryDate >= oneMonthAhead;
  };

  const isBeforeTwentyYears = (dateString: string) => {
    const expiryDate = new Date(dateString);
    const twentyYearsAhead = startOfDay(addYears(new Date(), 20));
    return expiryDate <= twentyYearsAhead;
  };

  return z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.expiryDate'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.expiryDate'),
      }),
    })
    .refine(
      expiryDateString => {
        return isOneMonthAhead(expiryDateString) && isBeforeTwentyYears(expiryDateString);
      },
      {
        message: t('rbcv2.errors.expiryDateRange', {
          fieldName: t('rbcv2.fieldLabels.expiryDate'),
        }),
      },
    );
};

export const schoolSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.school'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.school'),
      }),
    })
    .trim()
    .min(
      DEFAULT_MIN_STRING_LENGTH,
      t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.school'),
      }),
    )
    .max(
      SCHOOL_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.school'),
        maxLength: SCHOOL_MAX_LENGTH,
      }),
    );

export const provinceOfStudySchema = (t: TFunction) =>
  z.enum(VALID_PROVINCE_VALUES, {
    required_error: t('rbcv2.errors.fieldRequired', {
      fieldName: t('rbcv2.fieldLabels.provinceOfStudy'),
    }),
    invalid_type_error: t('rbc.errors.valid', {
      fieldName: t('rbcv2.fieldLabels.provinceOfStudy'),
    }),
  });

export const expectedArrivalDateSchema = (t: TFunction) => {
  const isAtLeastOneWeekAhead = (dateString: string) => {
    const date = new Date(dateString);
    const oneWeekAhead = startOfDay(addDays(new Date(), 7));
    return date >= oneWeekAhead;
  };

  const isNoMoreThanEighteenMonthsAhead = (dateString: string) => {
    const date = new Date(dateString);
    const eighteenMonthsAhead = startOfDay(addMonths(new Date(), 18));
    return date <= eighteenMonthsAhead;
  };

  return z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.expectedArrivalDate'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.expectedArrivalDate'),
      }),
    })
    .refine(dateStr => isAtLeastOneWeekAhead(dateStr) && isNoMoreThanEighteenMonthsAhead(dateStr), {
      message: t('rbcv2.errors.expectedArrivalDateRange', {
        fieldName: t('rbcv2.fieldLabels.expectedArrivalDate'),
      }),
    });
};

export const expectedGraduationMonthSchema = (t: TFunction) =>
  z.enum(VALID_MONTH_VALUES, {
    required_error: t('rbcv2.errors.fieldRequired', {
      fieldName: t('rbcv2.fieldLabels.expectedGraduationMonth'),
    }),
    invalid_type_error: t('rbcv2.errors.valid', {
      fieldName: t('rbcv2.fieldLabels.expectedGraduationMonth'),
    }),
  });

export const expectedGraduationYearSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.expectedGraduationYear'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.expectedGraduationYear'),
      }),
    })
    .trim()
    .min(
      DEFAULT_MIN_STRING_LENGTH,
      t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.expectedGraduationYear'),
      }),
    );

export const isThirdPartyInvestmentSchema = (t: TFunction) =>
  z.enum(['Yes', 'No'], {
    required_error: t('rbcv2.errors.yesOrNoRequired'),
    invalid_type_error: t('rbcv2.errors.validSelection'),
  });

export const isCanadianResidentForTaxSchema = (t: TFunction) =>
  z.enum(['Yes', 'No'], {
    required_error: t('rbcv2.errors.yesOrNoRequired'),
    invalid_type_error: t('rbcv2.errors.validSelection'),
  });

export const canadianTaxProvinceSchema = (t: TFunction) =>
  z.union([z.enum(VALID_PROVINCE_VALUES), z.literal('')], {
    required_error: t('rbcv2.errors.canadianTaxProvinceRequired'),
    invalid_type_error: t('rbc.errors.valid', {
      fieldName: t('rbcv2.fieldLabels.canadianTaxProvince'),
    }),
  });

export const isOtherCountryResidentForTaxSchema = (t: TFunction) =>
  z.enum(['Yes', 'No'], {
    required_error: t('rbcv2.errors.yesOrNoRequired'),
    invalid_type_error: t('rbcv2.errors.validSelection'),
  });

export const hasPoliticallyImportantPositionsSchema = (t: TFunction) =>
  z.enum(['Yes', 'No'], {
    required_error: t('rbcv2.errors.yesOrNoRequired'),
    invalid_type_error: t('rbcv2.errors.validSelection'),
  });

export const politicallyImportantFullNameSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.politicallyImportantFullName'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.politicallyImportantFullName'),
      }),
    })
    // Conditionally Rendered fields cannot have a min length
    .max(
      POLITICALLY_IMPORTANT_FULL_NAME_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.politicallyImportantFullName'),
        maxLength: POLITICALLY_IMPORTANT_FULL_NAME_MAX_LENGTH,
      }),
    );

export const politicallyImportantRelationshipSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.politicallyImportantRelationship'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.politicallyImportantRelationship'),
      }),
    })
    // Conditionally Rendered fields cannot have a min length
    .max(
      POLITICALLY_IMPORTANT_RELATIONSHIP_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.politicallyImportantRelationship'),
        maxLength: POLITICALLY_IMPORTANT_RELATIONSHIP_MAX_LENGTH,
      }),
    );

export const politicallyImportantRoleSchema = (t: TFunction) =>
  z
    .string({
      required_error: htmlDecode(
        t('rbcv2.errors.fieldRequired', {
          fieldName: t('rbcv2.fieldLabels.politicallyImportantRole'),
        }),
      ),
      invalid_type_error: htmlDecode(
        t('rbcv2.errors.valid', {
          fieldName: t('rbcv2.fieldLabels.politicallyImportantRole'),
        }),
      ),
    })
    // Conditionally Rendered fields cannot have a min length
    .max(
      POLITICALLY_IMPORTANT_ROLE_MAX_LENGTH,
      htmlDecode(
        t('rbcv2.errors.maxLength', {
          fieldName: t('rbcv2.fieldLabels.politicallyImportantRole'),
          maxLength: POLITICALLY_IMPORTANT_ROLE_MAX_LENGTH,
        }),
      ),
    );

export const sourceOfWealthSchema = (t: TFunction) =>
  z.array(z.string()).refine(data => data.every(item => SOURCE_OF_WEALTH_LIST.includes(item)), {
    message: t('rbcv2.errors.valid', {
      fieldName: t('rbcv2.fieldLabels.sourceOfWealth'),
    }),
  });

export const gicFundSourceSchema = (t: TFunction) =>
  z.array(z.string()).refine(data => data.every(item => GIC_FUND_SOURCE_LIST.includes(item)), {
    message: t('rbcv2.errors.valid', {
      fieldName: t('rbcv2.fieldLabels.gicFundSource'),
    }),
  });

export const sourceOfWealthOtherSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.sourceOfWealthOther'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.sourceOfWealthOther'),
      }),
    })
    .max(
      SOURCE_OF_WEALTH_OTHER_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.sourceOfWealthOther'),
        maxLength: SOURCE_OF_WEALTH_OTHER_MAX_LENGTH,
      }),
    );

export const gicFundSourceOtherSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('rbcv2.errors.fieldRequired', {
        fieldName: t('rbcv2.fieldLabels.gicFundSourceOther'),
      }),
      invalid_type_error: t('rbcv2.errors.valid', {
        fieldName: t('rbcv2.fieldLabels.gicFundSourceOther'),
      }),
    })
    .max(
      GIC_FUND_SOURCE_OTHER_MAX_LENGTH,
      t('rbcv2.errors.maxLength', {
        fieldName: t('rbcv2.fieldLabels.gicFundSourceOther'),
        maxLength: GIC_FUND_SOURCE_OTHER_MAX_LENGTH,
      }),
    );

export const fileSchema = (t: TFunction) =>
  z
    .array(
      z
        .object({
          file: z.instanceof(File).refine(file => SUPPORTED_MIME_TYPES.includes(file.type), {
            message: `File type must be one of ${SUPPORTED_FILE_TYPES_STR('or')}`,
          }),
          id: z.string().optional(),
          errorMessage: z.string().optional(),
        })
        .refine(value => !value.errorMessage, t('rbcv2.errors.fileValid')),
    )
    .length(1, {
      message: 'One file is required',
    });

export const rbcGicV2FormSchema = (t: TFunction) =>
  z
    .object({
      givenName: givenNameSchema(t),
      surname: surnameSchema(t),
      letterOfAcceptanceFile: fileSchema(t),

      prefix: prefixSchema(t),
      dateOfBirth: dateOfBirthSchema(t),
      purpose: purposeSchema(t),
      employmentStatus: employmentStatusSchema(t),
      levelOfStudy: levelOfStudySchema(t),
      occupation: occupationSchema(t),
      occupationOther: occupationOtherSchema(t),
      companyName: companyNameSchema(t),
      companyPhoneNumber: companyPhoneNumberSchema(t),
      phoneNumber: phoneNumberSchema(t),
      isMobileNumber: isMobileNumberSchema(t),
      emailAddress: emailAddressSchema(t),
      confirmEmailAddress: confirmEmailAddressSchema(t),

      isSameAsResidentialAddress: z.boolean(),
      aptOrSuiteResidential: aptOrSuiteSchema(t),
      numberAndStreetNameResidential: numberAndStreetNameSchema(t),
      cityResidential: citySchema(t),
      stateOrProvinceResidential: stateOrProvinceSchema(t),
      countryResidential: countrySchema(t),
      postalCodeResidential: postalCodeSchema(t),
      aptOrSuiteMailing: aptOrSuiteSchema(t),
      numberAndStreetNameMailing: numberAndStreetNameSchema(t),
      cityMailing: citySchema(t),
      stateOrProvinceMailing: stateOrProvinceSchema(t),
      countryMailing: countrySchema(t),
      postalCodeMailing: postalCodeSchema(t),

      passportNumber: passportNumberSchema(t),
      issuingCountry: countrySchema(t),
      issuingPlaceOrAuthority: issuingPlaceOrAuthoritySchema(t),
      expiryDate: expiryDateSchema(t),

      school: schoolSchema(t),
      provinceOfStudy: provinceOfStudySchema(t),
      expectedArrivalDate: expectedArrivalDateSchema(t),

      expectedGraduationMonth: expectedGraduationMonthSchema(t),
      expectedGraduationYear: expectedGraduationYearSchema(t),

      isThirdPartyInvestment: isThirdPartyInvestmentSchema(t),
      isCanadianResidentForTax: isCanadianResidentForTaxSchema(t),
      canadianTaxProvince: canadianTaxProvinceSchema(t),
      isOtherCountryResidentForTax: isOtherCountryResidentForTaxSchema(t),
      otherCountryResidentForTaxCountry1: otherCountryResidentForTaxCountry1Schema(t),
      otherCountryResidentForTaxCountry2: otherCountryResidentForTaxCountry2Schema(t),
      otherCountryResidentForTaxCountry3: otherCountryResidentForTaxCountry3Schema(t),
      hasPoliticallyImportantPositions: hasPoliticallyImportantPositionsSchema(t),
      politicallyImportantFullName: politicallyImportantFullNameSchema(t),
      politicallyImportantRelationship: politicallyImportantRelationshipSchema(t),
      politicallyImportantRole: politicallyImportantRoleSchema(t),

      gicFundSource: gicFundSourceSchema(t),
      gicFundSourceOther: sourceOfWealthOtherSchema(t),
      sourceOfWealth: sourceOfWealthSchema(t),
      sourceOfWealthOther: sourceOfWealthOtherSchema(t),
    })
    .refine(
      data => !!data.givenName?.trim() || !!data.surname?.trim(),
      t('rbcv2.errors.eitherGivenNameOrSurname', {
        surname: t('rbcv2.fieldLabels.surname'),
        givenName: t('rbcv2.fieldLabels.givenName'),
      }),
    )
    .refine(
      data =>
        data[RBC_GIC_FORM_PROPERTIES.emailAddress.propertyName] ===
        data[RBC_GIC_FORM_PROPERTIES.confirmEmailAddress.propertyName],
      {
        message: t('rbcv2.errors.emailShouldMatch', {
          confirmEmailFieldName: t('rbcv2.fieldLabels.confirmEmailAddress'),
          emailFieldName: t('rbcv2.fieldLabels.emailAddress'),
        }),
        path: [RBC_GIC_FORM_PROPERTIES.confirmEmailAddress.propertyName], // path of error
      },
    )
    .refine(
      data =>
        (data.occupation !== OTHER_OCCUPATION && !data.occupationOther) ||
        (data.occupation === OTHER_OCCUPATION && data.occupationOther),
      {
        message: t('rbcv2.errors.additionOccupationDetailsRequired'),
        path: [RBC_GIC_FORM_PROPERTIES.occupationOther.propertyName], // path of error
      },
    )
    .refine(
      data =>
        (data.isCanadianResidentForTax === 'Yes' && data.canadianTaxProvince) ||
        (data.isCanadianResidentForTax === 'No' && !data.canadianTaxProvince),
      {
        message: t('rbcv2.errors.canadianTaxProvinceRequired'),
        path: [RBC_GIC_FORM_PROPERTIES.canadianTaxProvince.propertyName],
      },
    )
    .refine(
      data =>
        !COUNTRIES_REQUIRE_PROVINCES.includes(data.countryResidential) ||
        data.stateOrProvinceResidential,
      {
        message: t('rbcv2.errors.provinceRequired'),
        path: [RBC_GIC_FORM_PROPERTIES.stateOrProvinceResidential.propertyName],
      },
    );
